/*
  Color Palette Generator – Shopify‑safe React widget
  --------------------------------------------------
  • 100% self‑contained single file (no external CSS, icons or frameworks)
  • Mounts into a Shadow DOM root to prevent layout/style conflicts
  • "Tailwind‑like" prefixed utilities implemented as FULL scoped CSS
  • Accessible (labels, roles, keyboard support, focus states, ARIA live regions)
  • Fast and mobile‑first; CSS animations (no Framer Motion required)
  • WCAG contrast checker, palette generation, image extraction, and exports
  • SEO tip: add a visible <h1> in Light DOM next to the mount for indexing

  HOW TO INSTALL ON SHOPIFY (no build tools)
  1) Online Store → Themes → Edit code → Assets → Add a new asset → Upload/Create `color-palette-generator.js` and paste this entire file.
  2) Wherever you want the tool: add
       <div id="cpg-mount"></div>
       <script type="module" src="{{ 'color-palette-generator.js' | asset_url }}"></script>
  3) Optional: for SEO, add a Light‑DOM heading and description near the mount.

  Multiple instances: duplicate the mount with unique ids. This script will auto‑mount to all elements matching MOUNT_SELECTOR.
*/

// ---------------------------
// Config
// ---------------------------
const MOUNT_SELECTOR = '#cpg-mount, .cpg-mount';
const WIDGET_TITLE = 'Color Palette Generator';

// ---------------------------
// Full, scoped utility CSS (Tailwind‑like w/ cpg: prefix)
// ---------------------------
const UTIL_CSS = `
  :host{all:initial}
  *,*::before,*::after{box-sizing:border-box}
  :host, body{font-family:ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji","Segoe UI Emoji"; color:#0f172a}
  :host{display:block}
  .cpg\\:container{max-width:1040px;margin-inline:auto;padding:16px}
  .cpg\\:card{background:#ffffff;border:1px solid #e5e7eb;border-radius:20px;box-shadow:0 2px 8px rgba(15,23,42,.04)}
  .cpg\\:section{padding:16px}
  .cpg\\:row{display:flex;gap:12px;flex-wrap:wrap;align-items:flex-start}
  .cpg\\:grid{display:grid;gap:12px}
  .cpg\\:grid-2{grid-template-columns:1fr}
  @media(min-width:768px){.md\\:grid-2{grid-template-columns:1fr 1fr}}
  .cpg\\:grid-3{grid-template-columns:1fr}
  @media(min-width:1024px){.lg\\:grid-3{grid-template-columns:1fr 1fr 1fr}}
  .cpg\\:h1{font-size:22px;line-height:1.2;margin:0 0 8px;font-weight:600;letter-spacing:-.01em}
  .cpg\\:h2{font-size:16px;line-height:1.3;margin:0 0 6px;font-weight:600}
  .cpg\\:muted{color:#64748b}
  .cpg\\:small{font-size:12px}
  .cpg\\:mono{font-family:ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace}
  .cpg\\:kbd{border:1px solid #e5e7eb;border-radius:6px;padding:.2rem .45rem;background:#f8fafc}

  /* spacing */
  .cpg\\:p-0{padding:0}.cpg\\:p-2{padding:8px}.cpg\\:p-3{padding:12px}.cpg\\:p-4{padding:16px}.cpg\\:p-6{padding:24px}
  .cpg\\:px-2{padding-inline:8px}.cpg\\:px-3{padding-inline:12px}.cpg\\:px-4{padding-inline:16px}
  .cpg\\:py-2{padding-block:8px}.cpg\\:py-3{padding-block:12px}
  .cpg\\:m-0{margin:0}.cpg\\:mt-1{margin-top:4px}.cpg\\:mt-2{margin-top:8px}.cpg\\:mt-3{margin-top:12px}.cpg\\:mt-4{margin-top:16px}.cpg\\:mt-6{margin-top:24px}.cpg\\:mb-2{margin-bottom:8px}.cpg\\:mb-3{margin-bottom:12px}.cpg\\:mb-4{margin-bottom:16px}
  .cpg\\:gap-1{gap:4px}.cpg\\:gap-2{gap:8px}.cpg\\:gap-3{gap:12px}.cpg\\:gap-4{gap:16px}

  /* layout */
  .cpg\\:flex{display:flex}.cpg\\:inline-flex{display:inline-flex}.cpg\\:items-center{align-items:center}
  .cpg\\:justify-between{justify-content:space-between}.cpg\\:justify-center{justify-content:center}
  .cpg\\:w-full{width:100%}.cpg\\:min-h-24{min-height:96px}
  .cpg\\:rounded-xl{border-radius:16px}.cpg\\:rounded-2xl{border-radius:20px}.cpg\\:rounded-full{border-radius:999px}
  .cpg\\:shadow{box-shadow:0 8px 24px rgba(2,6,23,.06)}
  .cpg\\:border{border:1px solid #e5e7eb}.cpg\\:border-t{border-top:1px solid #e5e7eb}

  /* buttons */
  .cpg\\:btn{appearance:none;border:1px solid #e5e7eb;background:#fff;border-radius:999px;padding:10px 14px;font-size:14px;line-height:1;font-weight:500;cursor:pointer;transition:transform .08s ease, background .2s ease, box-shadow .2s ease}
  .cpg\\:btn:hover{background:#f8fafc}
  .cpg\\:btn:active{transform:scale(.98)}
  .cpg\\:btn-primary{border-color:#0ea5e9;background:#0ea5e9;color:#fff}
  .cpg\\:btn-primary:hover{background:#0284c7}
  .cpg\\:btn-ghost{border-color:transparent;background:transparent}
  .cpg\\:btn-outline{background:#fff;border-color:#e5e7eb}
  .cpg\\:btn-block{width:100%}

  /* inputs */
  .cpg\\:field{display:flex;flex-direction:column;gap:6px}
  .cpg\\:label{font-size:12px;color:#334155}
  .cpg\\:input,.cpg\\:select,.cpg\\:textarea{width:100%;border:1px solid #e5e7eb;border-radius:12px;padding:10px 12px;font-size:14px;background:#fff;transition:border .2s ease, box-shadow .2s ease}
  .cpg\\:input:focus,.cpg\\:select:focus,.cpg\\:textarea:focus{outline:none;border-color:#0ea5e9;box-shadow:0 0 0 3px rgba(14,165,233,.2)}
  .cpg\\:range{appearance:none;width:100%;height:4px;border-radius:999px;background:#e5e7eb;outline:none}
  .cpg\\:range::-webkit-slider-thumb{appearance:none;width:18px;height:18px;border-radius:999px;background:#0ea5e9;border:none}

  /* swatches */
  .cpg\\:swatch{border-radius:16px;border:1px solid rgba(2,6,23,.06);overflow:hidden;position:relative}
  .cpg\\:swatch-body{min-height:110px}
  .cpg\\:swatch-meta{display:flex;align-items:center;justify-content:space-between;padding:10px 12px;border-top:1px solid rgba(2,6,23,.06);background:rgba(255,255,255,.85);backdrop-filter:saturate(120%) blur(6px)}
  .cpg\\:badge{font-size:12px;border:1px solid #e5e7eb;border-radius:999px;padding:4px 8px;background:#fff}
  .cpg\\:pill{border-radius:999px;padding:4px 8px;font-size:12px}
  .cpg\\:ok{background:#e6f9ed;color:#166534}
  .cpg\\:bad{background:#fee2e2;color:#991b1b}

  /* sheets / dialogs */
  .cpg\\:sheet{position:fixed;inset:0;display:none}
  .cpg\\:sheet[cpg-open]{display:block}
  .cpg\\:overlay{position:absolute;inset:0;background:rgba(0,0,0,.32);animation:cpg-fade .2s ease}
  .cpg\\:panel{position:absolute;inset:auto 0 0 0;background:#fff;border-radius:16px 16px 0 0;box-shadow:0 -10px 30px rgba(2,6,23,.08);padding:16px;animation:cpg-slide-up .22s ease}
  @media(min-width:768px){.cpg\\:panel{inset:auto auto 0 50%;transform:translateX(-50%);width:540px;border-radius:20px}}

  /* utilities */
  .cpg\\:sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}
  .cpg\\:cursor-copy{cursor:copy}
  .cpg\\:select-none{user-select:none}

  /* animations */
  @keyframes cpg-fade{from{opacity:0}to{opacity:1}}
  @keyframes cpg-slide-up{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
`;

// ---------------------------
// React UMD fallback loader (if store doesn’t already ship React 18)
// ---------------------------
function ensureReact() {
  return new Promise((resolve, reject) => {
    if (window.React && window.ReactDOM && window.ReactDOM.createRoot) return resolve();
    const add = (src) => new Promise((res, rej) => { const s = document.createElement('script'); s.src = src; s.async = true; s.crossOrigin = 'anonymous'; s.onload = res; s.onerror = rej; document.head.appendChild(s); });
    add('https://unpkg.com/react@18/umd/react.production.min.js')
      .then(() => add('https://unpkg.com/react-dom@18/umd/react-dom.production.min.js'))
      .then(resolve)
      .catch(reject);
  });
}

// ---------------------------
// Color utilities
// ---------------------------
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const hexToRgb = (hex) => {
  let h = hex.replace('#','').trim();
  if (h.length === 3) h = h.split('').map(ch=>ch+ch).join('');
  const n = parseInt(h, 16);
  return { r:(n>>16)&255, g:(n>>8)&255, b:n&255 };
};
const rgbToHex = ({r,g,b}) => {
  const toHex = (n)=>n.toString(16).padStart(2,'0');
  return `#${toHex(clamp(Math.round(r),0,255))}${toHex(clamp(Math.round(g),0,255))}${toHex(clamp(Math.round(b),0,255))}`.toUpperCase();
};
const rgbToHsl = ({r,g,b}) => {
  r/=255; g/=255; b/=255;
  const max = Math.max(r,g,b), min = Math.min(r,g,b);
  let h, s, l = (max+min)/2;
  if (max === min) { h=s=0; }
  else {
    const d = max - min;
    s = l > .5 ? d / (2 - max - min) : d / (max + min);
    switch(max){
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h *= 60;
  }
  return { h: h||0, s: s*100, l: l*100 };
};
const hslToRgb = ({h,s,l}) => {
  h/=360; s/=100; l/=100;
  const hue2rgb=(p,q,t)=>{ if(t<0) t+=1; if(t>1) t-=1; if(t<1/6) return p+(q-p)*6*t; if(t<1/2) return q; if(t<2/3) return p+(q-p)*(2/3 - t)*6; return p; };
  let r,g,b;
  if(s===0){ r=g=b=l; }
  else{
    const q = l < .5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p,q,h+1/3);
    g = hue2rgb(p,q,h);
    b = hue2rgb(p,q,h-1/3);
  }
  return { r: Math.round(r*255), g: Math.round(g*255), b: Math.round(b*255) };
};
const luminance = ({r,g,b}) => {
  const f = (u)=>{u/=255; return u<=0.03928 ? u/12.92 : Math.pow((u+0.055)/1.055, 2.4)};
  const [R,G,B] = [f(r),f(g),f(b)];
  return 0.2126*R + 0.7152*G + 0.0722*B;
};
const contrastRatio = (fgHex, bgHex) => {
  const L1 = luminance(hexToRgb(fgHex));
  const L2 = luminance(hexToRgb(bgHex));
  const [A,B] = L1 > L2 ? [L1,L2] : [L2,L1];
  return (A + 0.05) / (B + 0.05);
};

// Palette generation helpers
const rotateHue = (hex, deg) => { const hsl = rgbToHsl(hexToRgb(hex)); return rgbToHex(hslToRgb({h:(hsl.h+deg+360)%360, s:hsl.s, l:hsl.l})); };
const adjust = (hex, {s=0,l=0}={}) => { const hsl = rgbToHsl(hexToRgb(hex)); return rgbToHex(hslToRgb({h:hsl.h, s:clamp(hsl.s+s,0,100), l:clamp(hsl.l+l,0,100)})); };
function buildPalette(seed, mode, count){
  const base = seed;
  const steps = Math.max(2, Math.min(10, count||5));
  const out = [];
  const push = (hex)=> out.push(hex.toUpperCase());
  if (mode === 'monochrome'){
    const hsl = rgbToHsl(hexToRgb(base));
    for(let i=0;i<steps;i++){
      const l = clamp(8 + (i*(84/(steps-1))), 5, 95);
      push(rgbToHex(hslToRgb({h:hsl.h, s:hsl.s, l})));
    }
  } else if (mode === 'complementary'){
    const comp = rotateHue(base,180);
    for(let i=0;i<steps;i++){
      const t = i/(steps-1);
      // interpolate hues between base and complement
      const h0 = rgbToHsl(hexToRgb(base));
      const h1 = rgbToHsl(hexToRgb(comp));
      const h = (h0.h + (h1.h - h0.h)*t + 360) % 360;
      const s = clamp(h0.s + (h1.s - h0.s)*t, 0, 100);
      const l = clamp(h0.l + (h1.l - h0.l)*t, 0, 100);
      push(rgbToHex(hslToRgb({h,s,l})));
    }
  } else if (mode === 'analogous'){
    const spread = 60; // ±30°
    for(let i=0;i<steps;i++){
      const t = (i/(steps-1)) - .5; // -0.5..0.5
      push(rotateHue(base, t*spread));
    }
  } else if (mode === 'triadic'){
    const a = rotateHue(base, -120), b = base, c = rotateHue(base, 120);
    const list = [a,b,c];
    for(let i=0;i<steps;i++){ push(list[i % 3]); }
  } else if (mode === 'tetradic'){
    const list = [base, rotateHue(base,90), rotateHue(base,180), rotateHue(base,270)];
    for(let i=0;i<steps;i++){ push(list[i % 4]); }
  } else {
    for(let i=0;i<steps;i++){ push(base); }
  }
  return out;
}

// Image color extraction (simple quantization)
function extractColorsFromImage(img, count=5){
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const w = canvas.width = 200; const h = canvas.height = Math.round(img.naturalHeight * (w / img.naturalWidth));
  ctx.drawImage(img, 0, 0, w, h);
  const data = ctx.getImageData(0,0,w,h).data;
  const buckets = new Map();
  for(let i=0;i<data.length;i+=4){
    const r=data[i], g=data[i+1], b=data[i+2], a=data[i+3];
    if (a < 200) continue; // ignore transparency
    // reduce color space for bucketing
    const key = `${r>>3}_${g>>3}_${b>>3}`;
    const val = buckets.get(key) || { r:0,g:0,b:0,n:0 };
    val.r+=r; val.g+=g; val.b+=b; val.n++;
    buckets.set(key, val);
  }
  const list = Array.from(buckets.values()).map(v=>({ r:Math.round(v.r/v.n), g:Math.round(v.g/v.n), b:Math.round(v.b/v.n), n:v.n }));
  list.sort((a,b)=>b.n-a.n);
  return list.slice(0,count).map(rgbToHex);
}

// Clipboard & file helpers
async function copyText(text){
  try{ await navigator.clipboard.writeText(text); return true; }catch{ return false; }
}
function download(filename, text){
  const blob = new Blob([text], {type:'text/plain'});
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename; a.click(); URL.revokeObjectURL(a.href);
}
function downloadPNGFromPalette(colors){
  const size = 200, pad=2; const canvas = document.createElement('canvas');
  canvas.width = size * colors.length; canvas.height = size;
  const ctx = canvas.getContext('2d');
  colors.forEach((hex,i)=>{ ctx.fillStyle = hex; ctx.fillRect(i*size,0,size,size); });
  const a = document.createElement('a'); a.download = 'palette.png'; a.href = canvas.toDataURL('image/png'); a.click();
}
function downloadSVGFromPalette(colors){
  const sw = 160, sh = 160; const svg = `<?xml version="1.0"?><svg xmlns="http://www.w3.org/2000/svg" width="${sw*colors.length}" height="${sh}">${colors.map((c,i)=>`<rect x="${i*sw}" y="0" width="${sw}" height="${sh}" fill="${c}"/>`).join('')}</svg>`;
  download('palette.svg', svg);
}

// ---------------------------
// React components
// ---------------------------
function App(){
  const React = window.React;
  const {useMemo, useState, useRef, useEffect} = React;

  const [seed, setSeed] = useState('#4F46E5');
  const [mode, setMode] = useState('analogous');
  const [count, setCount] = useState(5);
  const [colors, setColors] = useState(()=>buildPalette('#4F46E5','analogous',5));
  const [activeBg, setActiveBg] = useState('#FFFFFF');
  const [activeFg, setActiveFg] = useState('#0F172A');
  const [toast, setToast] = useState('');

  useEffect(()=>{ setColors(buildPalette(seed, mode, count)); }, [seed, mode, count]);

  const ratios = useMemo(()=>{
    return colors.map(c=>({ c, ratio: Number(contrastRatio(activeFg, c)).toFixed(2), ratioOnBg: Number(contrastRatio(activeFg, activeBg)).toFixed(2) }));
  }, [colors, activeFg, activeBg]);

  const onCopy = async (payload, label='Copied!')=>{ const ok = await copyText(payload); setToast(ok?label:'Copy failed'); setTimeout(()=>setToast(''), 1400); };

  const onImagePick = (file)=>{
    const img = new Image();
    img.onload = ()=>{ const palette = extractColorsFromImage(img, count); setSeed(palette[0]||seed); setColors(palette); };
    img.src = URL.createObjectURL(file);
  };

  const exportCssVars = () => {
    const lines = colors.map((c,i)=>`  --color-${i+1}: ${c};`);
    const css = `:root{\n${lines.join('\n')}\n}`;
    download('palette.css', css);
  };
  const exportJson = () => download('palette.json', JSON.stringify({ seed, mode, count, colors }, null, 2));

  return window.React.createElement('section', { className:'cpg:container', role:'region', 'aria-label': WIDGET_TITLE },
    window.React.createElement('div', { className:'cpg:card cpg:section cpg:shadow' },
      window.React.createElement('header', { className:'cpg:mb-3' },[
        window.React.createElement('h1', { className:'cpg:h1', key:'h' }, WIDGET_TITLE),
        window.React.createElement('p', { className:'cpg:muted cpg:small', key:'p' }, 'Generate accessible, harmonious palettes. Extract from images, check WCAG contrast, and export for your project.')
      ]),

      // controls
      window.React.createElement('div', { className:'cpg:grid md:grid-2 cpg:mb-4' }, [
        window.React.createElement('div', { key:'left', className:'cpg:card cpg:section' }, [
          window.React.createElement('div', { className:'cpg:field cpg:mb-3', key:'seed' }, [
            window.React.createElement('label', { className:'cpg:label', htmlFor:'seed' }, 'Seed color'),
            window.React.createElement('input', { id:'seed', className:'cpg:input', type:'text', value:seed, onChange:e=>setSeed(e.target.value), placeholder:'#4F46E5', inputMode:'text', 'aria-describedby':'seed-help' }),
            window.React.createElement('div', { id:'seed-help', className:'cpg:muted cpg:small' }, 'Enter HEX (e.g., #1A2B3C).')
          ]),

          window.React.createElement('div', { className:'cpg:row cpg:mb-3', key:'pickers' }, [
            window.React.createElement('input', { key:'color', type:'color', className:'cpg:input', value:seed, onChange:e=>setSeed(e.target.value), 'aria-label':'Pick seed color' }),
            window.React.createElement('div', { className:'cpg:field', style:{minWidth:'160px'}, key:'mode' }, [
              window.React.createElement('label', { className:'cpg:label', htmlFor:'mode' }, 'Harmony'),
              window.React.createElement('select', { id:'mode', className:'cpg:select', value:mode, onChange:e=>setMode(e.target.value) }, [
                ['analogous','Analogous'],['complementary','Complementary'],['monochrome','Monochrome'],['triadic','Triadic'],['tetradic','Tetradic']
              ].map(([v,l])=>window.React.createElement('option', { key:v, value:v }, l)))
            ]),
            window.React.createElement('div', { className:'cpg:field', style:{minWidth:'120px'}, key:'count' }, [
              window.React.createElement('label', { className:'cpg:label', htmlFor:'count' }, 'Colors'),
              window.React.createElement('input', { id:'count', className:'cpg:range', type:'range', min:3, max:10, value:count, onChange:e=>setCount(parseInt(e.target.value)) }),
              window.React.createElement('div', { className:'cpg:small cpg:muted', 'aria-live':'polite' }, `${count} colors`)
            ])
          ]),

          window.React.createElement('div', { className:'cpg:row cpg:gap-2' }, [
            window.React.createElement('label', { className:'cpg:btn cpg:btn-outline cpg:inline-flex cpg:items-center', key:'upload' }, [
              'Extract from image',
              window.React.createElement('input', { type:'file', accept:'image/*', onChange:e=>e.target.files && onImagePick(e.target.files[0]), style:{display:'none'} })
            ]),
            window.React.createElement('button', { className:'cpg:btn cpg:btn-outline', onClick:()=>{ setSeed('#4F46E5'); setMode('analogous'); setCount(5);} }, 'Reset')
          ])
        ]),

        window.React.createElement('div', { key:'right', className:'cpg:card cpg:section' }, [
          window.React.createElement('div', { className:'cpg:grid lg:grid-3' }, [
            window.React.createElement('div', { className:'cpg:field', key:'fg' }, [
              window.React.createElement('label', { className:'cpg:label', htmlFor:'fg' }, 'Text color'),
              window.React.createElement('input', { id:'fg', className:'cpg:input', type:'text', value:activeFg, onChange:e=>setActiveFg(e.target.value) }),
              window.React.createElement('input', { type:'color', className:'cpg:input', value:activeFg, onChange:e=>setActiveFg(e.target.value), 'aria-label':'Pick text color'})
            ]),
            window.React.createElement('div', { className:'cpg:field', key:'bg' }, [
              window.React.createElement('label', { className:'cpg:label', htmlFor:'bg' }, 'Background'),
              window.React.createElement('input', { id:'bg', className:'cpg:input', type:'text', value:activeBg, onChange:e=>setActiveBg(e.target.value) }),
              window.React.createElement('input', { type:'color', className:'cpg:input', value:activeBg, onChange:e=>setActiveBg(e.target.value), 'aria-label':'Pick background color'})
            ]),
            window.React.createElement('div', { className:'cpg:field', key:'export' }, [
              window.React.createElement('label', { className:'cpg:label' }, 'Export'),
              window.React.createElement('div', { className:'cpg:row cpg:gap-2' }, [
                window.React.createElement('button', { className:'cpg:btn', onClick:()=>onCopy(colors.join(', '), 'HEX copied') }, 'Copy HEX'),
                window.React.createElement('button', { className:'cpg:btn', onClick:()=>onCopy(colors.map(c=>{const {r,g,b}=hexToRgb(c);return `rgb(${r}, ${g}, ${b})`}).join(', '), 'RGB copied') }, 'Copy RGB')
              ]),
              window.React.createElement('div', { className:'cpg:row cpg:gap-2 cpg:mt-2' }, [
                window.React.createElement('button', { className:'cpg:btn cpg:btn-outline', onClick:exportCssVars }, 'Download CSS'),
                window.React.createElement('button', { className:'cpg:btn cpg:btn-outline', onClick:exportJson }, 'Download JSON'),
                window.React.createElement('button', { className:'cpg:btn cpg:btn-outline', onClick:()=>downloadPNGFromPalette(colors) }, 'PNG'),
                window.React.createElement('button', { className:'cpg:btn cpg:btn-outline', onClick:()=>downloadSVGFromPalette(colors) }, 'SVG')
              ])
            ])
          ])
        ])
      ]),

      // palette display
      window.React.createElement('section', { className:'cpg:grid md:grid-2 cpg:mt-4' }, [
        window.React.createElement('div', { key:'swatches', className:'cpg:card cpg:section' }, [
          window.React.createElement('h2', { className:'cpg:h2 cpg:mb-2' }, 'Palette'),
          window.React.createElement('div', { className:'cpg:grid lg:grid-3' }, colors.map((hex,i)=>
            window.React.createElement('article', { key:hex+i, className:'cpg:swatch cpg:shadow', role:'group', 'aria-label':`Color ${hex}` }, [
              window.React.createElement('div', { className:'cpg:swatch-body', style:{background:hex} }),
              window.React.createElement('div', { className:'cpg:swatch-meta' }, [
                window.React.createElement('button', { className:'cpg:btn cpg:btn-ghost cpg:mono cpg:cursor-copy', onClick:()=>onCopy(hex, `${hex} copied`) }, hex),
                (()=>{ const r = contrastRatio(activeFg, hex); const ok = r>=4.5; return window.React.createElement('span', { className:`cpg:pill ${ok?'cpg:ok':'cpg:bad'}`, title:`Contrast with text ${activeFg}: ${r.toFixed(2)}:1` }, `${r.toFixed(2)}:1`); })()
              ])
            ])
          ))
        ]),

        window.React.createElement('div', { key:'checker', className:'cpg:card cpg:section' }, [
          window.React.createElement('h2', { className:'cpg:h2 cpg:mb-2' }, 'Contrast checker'),
          window.React.createElement('div', { className:'cpg:rounded-xl cpg:border cpg:min-h-24', style:{background:activeBg, padding:16} }, [
            window.React.createElement('div', { style:{color:activeFg} }, [
              window.React.createElement('p', { className:'cpg:mb-2' }, 'The quick brown fox jumps over the lazy dog.'),
              window.React.createElement('button', { className:'cpg:btn cpg:btn-primary' }, 'Primary button')
            ])
          ]),
          (()=>{ const r = contrastRatio(activeFg, activeBg); const aa = r>=4.5; const aaa = r>=7; return window.React.createElement('div', { className:'cpg:row cpg:mt-3' }, [
            window.React.createElement('span', { className:`cpg:pill ${aa?'cpg:ok':'cpg:bad'}` }, `AA ${aa?'Pass':'Fail'}`),
            window.React.createElement('span', { className:`cpg:pill ${aaa?'cpg:ok':'cpg:bad'}` }, `AAA ${aaa?'Pass':'Fail'}`),
            window.React.createElement('span', { className:'cpg:badge cpg:mono' }, `${r.toFixed(2)}:1`)
          ]); })()
        ])
      ]),

      toast && window.React.createElement('div', { className:'cpg:mt-3', 'aria-live':'polite' }, window.React.createElement('div', { className:'cpg:badge' }, toast))
    )
  );
}

// ---------------------------
// Shadow DOM mount + style injection
// ---------------------------
function mountAll(){
  const mounts = document.querySelectorAll(MOUNT_SELECTOR);
  mounts.forEach((el, idx) => {
    if (el.__cpgMounted) return; el.__cpgMounted = true;
    const root = el.attachShadow({mode:'open'});
    const style = document.createElement('style'); style.textContent = UTIL_CSS; root.appendChild(style);
    const app = document.createElement('div'); root.appendChild(app);
    const h1 = document.createElement('h1'); h1.textContent = WIDGET_TITLE; h1.className = 'cpg:h1 cpg:mb-2'; const head = document.createElement('div'); head.className = 'cpg:container cpg:mt-2';
    // Keep heading inside Shadow for visual; for SEO consider a Light‑DOM <h1> near the mount.
    head.appendChild(h1); root.appendChild(head);
    const host = document.createElement('div'); host.className='cpg:container'; root.appendChild(host);

    // move app into host card
    host.appendChild(app);

    // render
    const r = window.ReactDOM.createRoot(app);
    r.render(window.React.createElement(App));
  });
}

// ---------------------------
// Bootstrap
// ---------------------------
(async function bootstrap(){
  try{ await ensureReact(); mountAll(); }catch(e){ console.error('CPG failed to load React', e); }
})();
